import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { format, formatDistanceToNow } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import toast from "react-hot-toast";

export function MedicationCard() {
  const [isLoading, setIsLoading] = useState(true);
  const [takenToday, setTakenToday] = useState(false);
  const [lastDose, setLastDose] = useState<any>(null);
  const [streak, setStreak] = useState(0);

  const fetchDoseStatus = async () => {
    try {
      const [latestRes, streakRes] = await Promise.all([
        fetch("/api/dose/latest"),
        fetch("/api/dose/streak")
      ]);

      const latestData = await latestRes.json();
      const streakData = await streakRes.json();

      setTakenToday(latestData.takenToday || false);
      setLastDose(latestData.dose);
      setStreak(streakData.streak || 0);
    } catch (error) {
      console.error("Failed to fetch dose status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDoseStatus();
  }, []);

  const handleCheckboxChange = async (checked: boolean) => {
    if (!checked || takenToday) return;

    try {
      const response = await fetch("/api/dose", {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      if (response.ok) {
        setTakenToday(true);
        await fetchDoseStatus();
        toast.success("Great job! Medication recorded.", {
          duration: 4000,
          position: "top-center",
          style: {
            background: "#10b981",
            color: "white",
            fontWeight: "bold",
          },
        });
      } else {
        toast.error("Failed to record dose. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to record dose. Please try again.");
      console.error("Failed to record dose:", error);
    }
  };

  const formatLastDoseTime = () => {
    if (!lastDose) return "No doses recorded yet";

    // SQLite returns timestamps without timezone info, they're in UTC
    // Append 'Z' to indicate UTC if not present
    const timestamp = lastDose.timestamp.includes('Z') || lastDose.timestamp.includes('+')
      ? lastDose.timestamp
      : lastDose.timestamp + 'Z';

    const doseDate = new Date(timestamp);
    const mstDate = toZonedTime(doseDate, "America/Denver");
    const now = new Date();
    const mstNow = toZonedTime(now, "America/Denver");

    // Check if it's today
    if (
      mstDate.getDate() === mstNow.getDate() &&
      mstDate.getMonth() === mstNow.getMonth() &&
      mstDate.getFullYear() === mstNow.getFullYear()
    ) {
      return `Today at ${format(mstDate, "h:mm a")}`;
    }

    // Check if it's yesterday
    const yesterday = new Date(mstNow);
    yesterday.setDate(yesterday.getDate() - 1);
    if (
      mstDate.getDate() === yesterday.getDate() &&
      mstDate.getMonth() === yesterday.getMonth() &&
      mstDate.getFullYear() === yesterday.getFullYear()
    ) {
      return `Yesterday at ${format(mstDate, "h:mm a")}`;
    }

    // Otherwise show relative time
    return formatDistanceToNow(doseDate, { addSuffix: true });
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-3xl font-bold text-center">
          Did you take your medicine today?
        </CardTitle>
        <CardDescription className="text-center text-lg mt-2">
          Check the box below to record your dose
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-center">
          <label
            htmlFor="medication-checkbox"
            className={`flex items-center space-x-4 p-6 rounded-lg border-2 transition-all cursor-pointer ${
              takenToday
                ? "bg-green-50 border-green-500"
                : "hover:bg-gray-50 border-gray-300"
            }`}
          >
            <Checkbox
              id="medication-checkbox"
              checked={takenToday}
              onCheckedChange={handleCheckboxChange}
              disabled={takenToday || isLoading}
              className="h-8 w-8"
            />
            <span className="text-xl font-medium">
              {takenToday ? "Medication taken today ✓" : "Click to confirm"}
            </span>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Last Dose</p>
            <p className="text-lg font-semibold">{formatLastDoseTime()}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Current Streak</p>
            <p className="text-lg font-semibold">
              {streak} {streak === 1 ? "day" : "days"} 🔥
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}